import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Evaluation UI State
 *
 * Manages ONLY client-side UI state for Evaluations module.
 * Server data is handled by TanStack Query hooks.
 *
 * This store contains:
 * - Section expansion state (evaluationsList, analytics)
 * - Status filter selection
 * - Manager view toggle
 * - Selected employee for manager view
 *
 * DO NOT store server data here (evaluations list, scores, etc.)
 */
interface EvaluationUIStore {
  // ================================
  // FOCUS MODE
  // ================================

  /** When true, AuthLayout hides sidebar and header */
  focusMode: boolean

  /** Enter focus mode (evaluation form takes over the screen) */
  enterFocusMode: () => void

  /** Exit focus mode (restore sidebar and header) */
  exitFocusMode: () => void

  // ================================
  // SECTION EXPANSION
  // ================================

  /** Expanded sections state */
  expandedSections: {
    evaluationsList: boolean
    analytics: boolean
  }

  /** Toggle section expansion */
  toggleSection: (section: keyof EvaluationUIStore['expandedSections']) => void

  // ================================
  // FILTERS
  // ================================

  /** Status filter */
  statusFilter: 'all' | 'draft' | 'submitted' | 'reviewed' | 'completed'

  /** Set status filter */
  setStatusFilter: (filter: EvaluationUIStore['statusFilter']) => void

  // ================================
  // MANAGER VIEW
  // ================================

  /** Manager view enabled */
  showManagerView: boolean

  /** Set manager view */
  setShowManagerView: (show: boolean) => void

  /** Selected employee ID (for manager view) */
  selectedEmployeeId: string | null

  /** Set selected employee */
  setSelectedEmployeeId: (id: string | null) => void

  // ================================
  // UTILITY
  // ================================

  /** Reset all UI state */
  reset: () => void
}

const initialState = {
  focusMode: false,
  expandedSections: {
    evaluationsList: true,
    analytics: false,
  },
  statusFilter: 'all' as const,
  showManagerView: false,
  selectedEmployeeId: null,
}

/**
 * Evaluation UI Store
 *
 * Persisted to localStorage so filter/view preferences survive page refreshes.
 * Expansion state is NOT persisted (resets on refresh for cleaner UX).
 */
export const useEvaluationUIStore = create<EvaluationUIStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Focus mode
      enterFocusMode: () => set({ focusMode: true }),
      exitFocusMode: () => set({ focusMode: false }),

      // Section expansion
      toggleSection: (section) =>
        set((state) => ({
          expandedSections: {
            ...state.expandedSections,
            [section]: !state.expandedSections[section],
          },
        })),

      // Filters
      setStatusFilter: (filter) => set({ statusFilter: filter }),

      // Manager view
      setShowManagerView: (show) => set({ showManagerView: show }),
      setSelectedEmployeeId: (id) => set({ selectedEmployeeId: id }),

      // Utility
      reset: () => set(initialState),
    }),
    {
      name: 'evaluation-ui-store', // localStorage key
      // Only persist filters and manager view (not expansion state)
      partialize: (state) => ({
        statusFilter: state.statusFilter,
        showManagerView: state.showManagerView,
        selectedEmployeeId: state.selectedEmployeeId,
      }),
    }
  )
)
